// src/components/SmartLists/SmartListsGridCell.jsx
import React, { useMemo, useRef, useState } from 'react';
import { Box } from '@mui/material';
import {
  GRID_ROW_HEIGHT,
  STATUS_OPTIONS,
} from './smartListsGridConfig';

/**
 * SmartListsGridCell
 * -----------------------------------------------------------------------------
 * Reusable spreadsheet cell primitive for the SmartLists workspace.
 *
 * PURPOSE
 *  • Render one editable spreadsheet cell
 *  • Support text / date-like-text / select cell types
 *  • Keep blank rows visually blank
 *  • Let the parent canvas control:
 *      - selection state
 *      - keyboard navigation
 *      - value updates
 *      - ref registration for focus management
 *
 * IMPORTANT
 *  • This component intentionally does NOT own spreadsheet selection logic
 *  • It does NOT own row/column sizing logic beyond respecting the width it is given
 *  • It does NOT persist anything by itself
 *  • It is intentionally lightweight so it can be reused across grouped SmartList columns
 *
 * BLANK ROW RULE
 *  • If a row is still a blank spreadsheet row:
 *      - Option remains editable
 *      - Hint may remain editable
 *      - Status / Launch Date / End Date stay visually empty
 *  • We do NOT show fake status/date controls until the row actually has an option
 *
 * DATE UX NOTE
 *  • We avoid showing the native browser calendar icon because it consumes too
 *    much cell width and makes the grid feel like a form instead of a spreadsheet.
 *  • When not editing, date cells render like plain spreadsheet text with the
 *    placeholder MM/DD/YYYY.
 *  • When focused, the cell switches into a real date input and tries to open
 *    the browser picker via showPicker() when supported.
 *
 * STATUS UX NOTE
 *  • We intentionally DO NOT render chips in the select cell.
 *  • The status cell behaves like a compact spreadsheet select/text field.
 *
 * DENSITY NOTE
 *  • Padding and font size are intentionally tight here to feel closer to
 *    Google Sheets.
 * -----------------------------------------------------------------------------
 */

const FONT_SIZE = '11px';
const CELL_HORIZONTAL_PADDING = '4px';
const SELECT_HORIZONTAL_PADDING = '2px';

const formatIsoToDisplay = (value) => {
  if (!value) return '';

  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [yyyy, mm, dd] = value.split('-');
    return `${mm}/${dd}/${yyyy}`;
  }

  return value;
};

const SmartListsGridCell = ({
  groupId,
  rowIndex,
  column,
  row,
  isSelected,
  registerCellRef,
  onSelectCell,
  onChangeValue,
  onKeyDown,
  onPaste,
}) => {
  const value = row?.[column.key] ?? '';
  const isBlankRow = row?.isBlankRow === true;

  const [isDateEditing, setIsDateEditing] = useState(false);
  const inputRef = useRef(null);

  const displayValue = useMemo(() => {
    return formatIsoToDisplay(value);
  }, [value]);

  const commonBoxSx = {
    width: `${column.width}px`,
    minWidth: `${column.width}px`,
    maxWidth: `${column.width}px`,
    height: `${GRID_ROW_HEIGHT}px`,

    // Lighter Sheets-like grid lines
    borderRight: '1px solid #e8eaed',
    borderBottom: '1px solid #e8eaed',

    boxSizing: 'border-box',
    backgroundColor: isSelected ? '#e3f2fd' : '#ffffff',
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  };

  const shouldHideEditorForBlankRow =
    isBlankRow &&
    ['lifecycleStatus', 'liveFrom', 'liveTo'].includes(column.key);

  if (shouldHideEditorForBlankRow) {
    return (
      <Box
        key={`${groupId}-${rowIndex}-${column.key}`}
        sx={commonBoxSx}
        onClick={() => onSelectCell(groupId, rowIndex, column.key)}
      />
    );
  }

  if (column.type === 'select') {
    return (
      <Box
        key={`${groupId}-${rowIndex}-${column.key}`}
        sx={commonBoxSx}
      >
        <select
          ref={(node) => registerCellRef(groupId, rowIndex, column.key, node)}
          value={value || ''}
          onFocus={() => onSelectCell(groupId, rowIndex, column.key)}
          onClick={() => onSelectCell(groupId, rowIndex, column.key)}
          onChange={(e) => onChangeValue(groupId, rowIndex, column.key, e.target.value)}
          onKeyDown={(e) => onKeyDown(e, groupId, rowIndex, column.key)}
          onPaste={(e) => onPaste?.(e, groupId, rowIndex, column.key)}
          style={{
            width: '100%',
            height: '100%',
            border: 'none',
            outline: 'none',
            background: 'transparent',
            fontSize: FONT_SIZE,
            padding: `0 ${SELECT_HORIZONTAL_PADDING}`,
            boxSizing: 'border-box',
            appearance: 'none',
            WebkitAppearance: 'none',
            MozAppearance: 'none',
            lineHeight: `${GRID_ROW_HEIGHT}px`,
            verticalAlign: 'middle',
          }}
        >
          <option value=""></option>
          {STATUS_OPTIONS.map((opt) => (
            <option key={`${groupId}-${column.key}-${opt.value}`} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </Box>
    );
  }

  if (column.type === 'date-text') {
    const handleDateFocus = () => {
      onSelectCell(groupId, rowIndex, column.key);
      setIsDateEditing(true);

      requestAnimationFrame(() => {
        try {
          inputRef.current?.showPicker?.();
        } catch (error) {
          // no-op
        }
      });
    };

    const handleDateBlur = () => {
      setIsDateEditing(false);
    };

    return (
      <Box
        key={`${groupId}-${rowIndex}-${column.key}`}
        sx={{
          ...commonBoxSx,
          '& input::-webkit-calendar-picker-indicator': {
            opacity: 0,
            display: 'none',
            WebkitAppearance: 'none',
          },
          '& input::-webkit-clear-button': {
            display: 'none',
          },
          '& input::-webkit-inner-spin-button': {
            display: 'none',
          },
        }}
      >
        <input
          ref={(node) => {
            inputRef.current = node;
            registerCellRef(groupId, rowIndex, column.key, node);
          }}
          type={isDateEditing ? 'date' : 'text'}
          value={isDateEditing ? (value || '') : displayValue}
          placeholder={isDateEditing ? '' : 'MM/DD/YYYY'}
          onFocus={handleDateFocus}
          onClick={() => onSelectCell(groupId, rowIndex, column.key)}
          onBlur={handleDateBlur}
          onChange={(e) => onChangeValue(groupId, rowIndex, column.key, e.target.value)}
          onKeyDown={(e) => onKeyDown(e, groupId, rowIndex, column.key)}
          onPaste={(e) => onPaste?.(e, groupId, rowIndex, column.key)}
          style={{
            width: `${column.width}px`,
            minWidth: `${column.width}px`,
            maxWidth: `${column.width}px`,
            height: `${GRID_ROW_HEIGHT}px`,
            border: 'none',
            borderRight: '1px solid #f1f1f1',
            borderBottom: '1px solid #f1f1f1',
            boxSizing: 'border-box',
            padding: `0 ${CELL_HORIZONTAL_PADDING}`,
            fontSize: FONT_SIZE,
            lineHeight: `${GRID_ROW_HEIGHT}px`,   // aligns placeholder/text vertically
            verticalAlign: 'middle',
            backgroundColor: isSelected ? '#e3f2fd' : '#ffffff',
            outline: 'none',
          }}
        />
      </Box>
    );
  }

  return (
    <input
      key={`${groupId}-${rowIndex}-${column.key}`}
      ref={(node) => registerCellRef(groupId, rowIndex, column.key, node)}
      type="text"
      value={value}
      onFocus={() => onSelectCell(groupId, rowIndex, column.key)}
      onClick={() => onSelectCell(groupId, rowIndex, column.key)}
      onChange={(e) => onChangeValue(groupId, rowIndex, column.key, e.target.value)}
      onKeyDown={(e) => onKeyDown(e, groupId, rowIndex, column.key)}
      onPaste={(e) => onPaste?.(e, groupId, rowIndex, column.key)}
      style={{
        width: `${column.width}px`,
        minWidth: `${column.width}px`,
        maxWidth: `${column.width}px`,
        height: `${GRID_ROW_HEIGHT}px`,
        border: 'none',
        borderRight: '1px solid #f1f1f1',
        borderBottom: '1px solid #f1f1f1',
        boxSizing: 'border-box',
        padding: `0 ${CELL_HORIZONTAL_PADDING}`,
        fontSize: FONT_SIZE,
        lineHeight: `${GRID_ROW_HEIGHT}px`,   // aligns text vertically like Sheets
        verticalAlign: 'middle',
        backgroundColor: isSelected ? '#e3f2fd' : '#ffffff',
        outline: 'none',
      }}
    />
  );
};

export default SmartListsGridCell;